// ─── Cloudové a lokální úložiště pro Poptávky Online ─────────────────────────
// Používá stejný projekt hk-software-c5e52 s plně oddělenou kořenovou kolekcí 'poptavky_project'
// (stejný vzor jako 'pudorys_project' u Půdorys Online), aby se nepletla do hlavního Admina.

import { initializeApp } from 'firebase/app'
import {
  initializeFirestore,
  collection,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  limit,
  serverTimestamp,
} from 'firebase/firestore'
import {
  getAuth,
  onAuthStateChanged,
  GoogleAuthProvider,
  OAuthProvider,
  linkWithCredential,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  User,
} from 'firebase/auth'
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage'
import type {
  AiInquiryDraft,
  AppUser,
  CompanyProfile,
  Inquiry,
  InquiryOffer,
  CompanyReview,
  CreditTransaction,
  FloorplanSnapshot,
  TradeCategory,
  UserRole,
} from './types/poptavky'
import { TRADE_CATEGORIES } from './types/poptavky'
import { buildFloorplanSnapshot, parsePudorysIdFromInput, DEMO_FLOORPLAN_TEMPLATES } from './utils/floorplanUtils'

export type { AppUser }

const currentHostname = typeof window !== 'undefined' ? window.location.hostname : ''
const CUSTOM_AUTH_DOMAINS = new Set([
  'nacenito.cz',
  'www.nacenito.cz',
  'firmy.nacenito.cz',
  'brigady.nacenito.cz',
  'brigadnici.nacenito.cz',
  'poptavky-online.web.app',
  'firmy-poptavky.web.app',
  'brigadnici-poptavky.web.app',
  'pudorysonline.cz',
  'www.pudorysonline.cz',
])

const resolvedAuthDomain = CUSTOM_AUTH_DOMAINS.has(currentHostname)
  ? currentHostname
  : 'hk-software-c5e52.firebaseapp.com'

const firebaseConfig = {
  apiKey: 'AIzaSyBVwLxgeTp3DiWHYRLceS-_s7yvcYFUAt0',
  authDomain: resolvedAuthDomain,
  databaseURL: 'https://hk-software-c5e52-default-rtdb.europe-west1.firebasedatabase.app',
  projectId: 'hk-software-c5e52',
  storageBucket: 'hk-software-c5e52.firebasestorage.app',
  messagingSenderId: '389675656528',
  appId: '1:389675656528:web:15477c34c905465b29f855',
  measurementId: 'G-KR4Z7CHMD4',
}

export const app = initializeApp(firebaseConfig)
export const db = initializeFirestore(app, {
  ignoreUndefinedProperties: true,
})
export const auth = getAuth(app)
export const storage = getStorage(app)
export const googleProvider = new OAuthProvider('oidc.nacenito')
googleProvider.addScope('openid')
googleProvider.addScope('email')
googleProvider.addScope('profile')
googleProvider.setCustomParameters({ prompt: 'select_account' })

let oauthPrewarmed = false
/**
 * Předehřeje OAuth popup resolver až ve chvíli, kdy uživatel najede myší / dotkne se tlačítka přihlášení,
 * aby se při prvotním načtení stránky nestahoval 135 KB auth/iframe.js a nebrzdil PageSpeed LCP.
 */
export function prewarmGoogleAuthPopup(): void {
  if (oauthPrewarmed || typeof window === 'undefined') return
  oauthPrewarmed = true
  try {
    const anyAuth = auth as any
    anyAuth._popupRedirectResolver?._initialize?.(anyAuth)?.catch?.(() => {})
  } catch {
    // ignorovat
  }
}

// Izolovaná kořenová kolekce pro Poptávky Online
export const ROOT_COLLECTION = 'poptavky_project'
// Kořenová kolekce sesterské aplikace Půdorys Online pro čtení návrhů
export const PUDORYS_ROOT_COLLECTION = 'pudorys_project'
export const PUDORYS_LEGACY_COLLECTION = 'pudorys_projects'

// Klíče pro okamžitou lokální mezipaměť (LocalStorage), aby aplikace fungovala bleskově
const LS_COMPANIES_KEY = 'poptavky_companies_v1'
const LS_INQUIRIES_KEY = 'poptavky_inquiries_v1'
const LS_OFFERS_KEY = 'poptavky_offers_v1'
const LS_REVIEWS_KEY = 'poptavky_reviews_v1'
const LS_TRANSACTIONS_KEY = 'poptavky_transactions_v1'
const LS_USERS_KEY = 'poptavky_users_v1'
const LS_SEEDED_KEY = 'poptavky_initial_seeded_v2'

/**
 * Zajistí existenci kontejnerových dokumentů pod `poptavky_project` (pouze pokud je uživatel přihlášen)
 */
export async function ensurePoptavkyContainers(): Promise<void> {
  if (!auth.currentUser) return
  try {
    const containers = ['users', 'companies', 'inquiries', 'offers', 'reviews', 'transactions']
    await Promise.all(
      containers.map((name) =>
        setDoc(
          doc(db, ROOT_COLLECTION, name),
          { app: 'NaceniTo.cz', section: name, updatedAt: serverTimestamp() },
          { merge: true }
        )
      )
    )
  } catch {
    // Tichý fallback
  }
}

/**
 * Vrátí aktuálně přihlášeného uživatele (bez volání signInAnonymously, které způsobovalo 400 chybu v konzoli).
 */
export async function ensureAuthSession(): Promise<User | null> {
  if (auth.currentUser) return auth.currentUser
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(auth.currentUser), 1200)
    const unsub = onAuthStateChanged(auth, (u) => {
      clearTimeout(timer)
      unsub()
      resolve(u || null)
    })
  })
}

export async function loginWithGoogle(): Promise<User> {
  try {
    const res = await signInWithPopup(auth, googleProvider)
    return res.user
  } catch (err: any) {
    if (err?.code === 'auth/popup-blocked') {
      await signInWithRedirect(auth, googleProvider)
      throw err
    }
    if (err?.code === 'auth/account-exists-with-different-credential') {
      const pendingCred = OAuthProvider.credentialFromError(err)
      const fallbackGoogle = new GoogleAuthProvider()
      fallbackGoogle.setCustomParameters({ prompt: 'select_account' })
      const res = await signInWithPopup(auth, fallbackGoogle)
      if (pendingCred) {
        await linkWithCredential(res.user, pendingCred).catch(() => {})
      }
      return res.user
    }
    throw err
  }
}

export async function logoutAccount(): Promise<void> {
  await signOut(auth)
}

// ─── NAHRÁVÁNÍ FOTOGRAFIÍ DO CLOUDOVÉHO ÚLOŽIŠTĚ S CANVAS KOMPRESÍ ───────────

/**
 * Zkomprimuje a zmenší fotografii (max. 1200 px, kvalita 0.82) a nahraje ji
 * do izolované složky v cloudovém úložišti: poptavky_project/${folder}/${entityId}/...
 * Pokud cloudové úložiště vrátí chybu oprávnění nebo není nasazené,
 * automaticky vrátí zkomprimovaný Base64 dataURL jako 100% spolehlivý fallback.
 */
export async function uploadProjectPhoto(
  file: File,
  folder: 'inquiries' | 'companies',
  entityId: string
): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Nepodařilo se přečíst vybraný soubor.'))
    reader.onload = () => {
      const rawDataUrl = reader.result as string
      try {
        const img = new Image()
        img.onerror = () => {
          // Pokud selže dekódování obrázku, vrátíme původní data
          resolve(rawDataUrl)
        }
        img.onload = async () => {
          try {
            const maxDimension = 1200
            let width = img.width
            let height = img.height

            if (width > maxDimension || height > maxDimension) {
              if (width > height) {
                height = Math.round((height * maxDimension) / width)
                width = maxDimension
              } else {
                width = Math.round((width * maxDimension) / height)
                height = maxDimension
              }
            }

            const canvas = document.createElement('canvas')
            canvas.width = width
            canvas.height = height
            const ctx = canvas.getContext('2d')

            if (!ctx) {
              resolve(rawDataUrl)
              return
            }

            // Vyplnění bílého pozadí pro případ průhlednosti
            ctx.fillStyle = '#ffffff'
            ctx.fillRect(0, 0, width, height)
            ctx.drawImage(img, 0, 0, width, height)

            // Okamžitý spolehlivý fallback ve formátu JPEG (kvalita 0.8)
            const fallbackDataUrl = canvas.toDataURL('image/jpeg', 0.8)

            // Vytvoření Blobu pro nahrání do Firebase Storage (JPEG kvalita 0.82)
            canvas.toBlob(
              async (blob) => {
                if (!blob) {
                  resolve(fallbackDataUrl)
                  return
                }

                // Očištění názvu souboru pro bezpečné cloudové uložení
                const cleanFileName =
                  file.name
                    .replace(/\.[^/.]+$/, '')
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .replace(/[^a-zA-Z0-9_-]/g, '_')
                    .slice(0, 40) || 'foto'

                const storagePath = `poptavky_project/${folder}/${entityId}/${Date.now()}_${cleanFileName}.jpg`

                try {
                  const storageRef = ref(storage, storagePath)
                  const snapshot = await uploadBytes(storageRef, blob, {
                    contentType: 'image/jpeg',
                  })
                  const downloadUrl = await getDownloadURL(snapshot.ref)
                  resolve(downloadUrl)
                } catch {
                  // Chyba oprávnění nebo nedostupné cloudové úložiště -> okamžitý funkční fallback
                  resolve(fallbackDataUrl)
                }
              },
              'image/jpeg',
              0.82
            )
          } catch {
            resolve(rawDataUrl)
          }
        }
        img.src = rawDataUrl
      } catch {
        resolve(rawDataUrl)
      }
    }
    reader.readAsDataURL(file)
  })
}

// ─── SPRÁVA UŽIVATELŮ A OPRÁVNĚNÍ (poptavky_project/users/items/{uid}) ─────────

/**
 * Synchronizuje a načte profil uživatele po přihlášení přes Google.
 * Pokud v poptavky_project/users/items ještě neexistuje žádný administrátor
 * (nebo jde o prvního přihlášeného uživatele), automaticky mu nastaví roli 'admin' a isAdmin: true.
 */
export async function syncAndLoadAppUser(user: User): Promise<AppUser> {
  const localUsers = readLocalList<AppUser>(LS_USERS_KEY)
  const existingLocal = localUsers.find((u) => u.uid === user.uid)
  const now = Date.now()

  let remoteData: Partial<AppUser> | null = null
  let anyAdminExists = false

  // 1. Zkusíme načíst existující záznam uživatele z Firestore
  try {
    const userDocRef = doc(db, ROOT_COLLECTION, 'users', 'items', user.uid)
    const snap = await getDoc(userDocRef)
    if (snap.exists()) {
      remoteData = snap.data() as Partial<AppUser>
    }
  } catch {
    // Tichý fallback při chybě sítě
  }

  // 2. Ověříme, zda již v systému existuje nějaký administrátor
  try {
    const adminQuery = query(
      collection(db, ROOT_COLLECTION, 'users', 'items'),
      where('isAdmin', '==', true),
      limit(1)
    )
    const adminSnap = await getDocs(adminQuery)
    if (!adminSnap.empty) {
      anyAdminExists = true
    } else {
      const roleAdminQuery = query(
        collection(db, ROOT_COLLECTION, 'users', 'items'),
        where('role', '==', 'admin'),
        limit(1)
      )
      const roleAdminSnap = await getDocs(roleAdminQuery)
      if (!roleAdminSnap.empty) {
        anyAdminExists = true
      }
    }
  } catch {
    // Lokální kontrola mezipaměti
    anyAdminExists = localUsers.some((u) => u.isAdmin || u.role === 'admin')
  }

  // 3. Zhodnocení administrátorských práv
  const isFirstOrAdmin =
    !anyAdminExists ||
    remoteData?.isAdmin === true ||
    remoteData?.role === 'admin' ||
    existingLocal?.isAdmin === true ||
    existingLocal?.role === 'admin'

  const finalRole: UserRole = isFirstOrAdmin
    ? 'admin'
    : (remoteData?.role || existingLocal?.role || 'client')

  const appUser: AppUser = {
    uid: user.uid,
    email: user.email || remoteData?.email || existingLocal?.email || '',
    displayName:
      user.displayName ||
      remoteData?.displayName ||
      existingLocal?.displayName ||
      (user.email ? user.email.split('@')[0] : 'Uživatel'),
    photoURL: user.photoURL || remoteData?.photoURL || existingLocal?.photoURL || undefined,
    phone: remoteData?.phone || existingLocal?.phone || undefined,
    role: finalRole,
    isAdmin: isFirstOrAdmin,
    companyId: remoteData?.companyId || existingLocal?.companyId || undefined,
    createdAt: remoteData?.createdAt || existingLocal?.createdAt || now,
    updatedAt: now,
  }

  // 4. Uložení do lokální mezipaměti
  const updatedLocal = localUsers.filter((u) => u.uid !== user.uid)
  updatedLocal.unshift(appUser)
  writeLocalList(LS_USERS_KEY, updatedLocal)

  // 5. Uložení do Firestore v izolované kolekci
  try {
    ensurePoptavkyContainers().catch(() => {})
    await setDoc(doc(db, ROOT_COLLECTION, 'users', 'items', user.uid), appUser, {
      merge: true,
    })
  } catch {
    // Lokální uložení zachováno
  }

  return appUser
}

/**
 * Načte seznam všech registrovaných uživatelů pro administrátorský přehled oprávnění.
 */
export async function loadAllAppUsers(): Promise<AppUser[]> {
  const localUsers = readLocalList<AppUser>(LS_USERS_KEY)
  if (!auth.currentUser) {
    return localUsers
  }

  try {
    const snap = await getDocs(
      query(collection(db, ROOT_COLLECTION, 'users', 'items'), limit(200))
    )
    if (!snap.empty) {
      const remoteUsers = snap.docs.map((d) => d.data() as AppUser)
      const map = new Map<string, AppUser>()
      localUsers.forEach((u) => map.set(u.uid, u))
      remoteUsers.forEach((u) => {
        const existing = map.get(u.uid)
        if (!existing || (u.updatedAt || 0) >= (existing.updatedAt || 0)) {
          map.set(u.uid, u)
        }
      })
      const merged = Array.from(map.values()).sort(
        (a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)
      )
      writeLocalList(LS_USERS_KEY, merged)
      return merged
    }
  } catch {
    // Ignorovat chybu sítě
  }

  return localUsers
}

/**
 * Nastaví uživatelskou roli a administrátorská práva konkrétnímu uživateli.
 */
export async function setAppUserRole(
  uid: string,
  role: 'client' | 'craftsman' | 'admin',
  isAdmin: boolean
): Promise<void> {
  const localUsers = readLocalList<AppUser>(LS_USERS_KEY)
  const now = Date.now()
  const idx = localUsers.findIndex((u) => u.uid === uid)

  if (idx >= 0) {
    localUsers[idx] = {
      ...localUsers[idx],
      role,
      isAdmin,
      updatedAt: now,
    }
  } else {
    localUsers.unshift({
      uid,
      email: '',
      displayName: 'Uživatel',
      role,
      isAdmin,
      createdAt: now,
      updatedAt: now,
    })
  }
  writeLocalList(LS_USERS_KEY, localUsers)

  try {
    ensurePoptavkyContainers().catch(() => {})
    await setDoc(
      doc(db, ROOT_COLLECTION, 'users', 'items', uid),
      { role, isAdmin, updatedAt: now },
      { merge: true }
    )
  } catch {
    // Tichý fallback
  }
}

// ─── INTEGRACE S PŮDORYS ONLINE ──────────────────────────────────────────────

/**
 * Načte uložené půdorysy přihlášeného uživatele přímo z `pudorys_project/projects/items`
 * + lokální projekty z LocalStorage (pokud běží na stejné doméně)
 */
export async function fetchUserFloorplansFromPudorys(): Promise<FloorplanSnapshot[]> {
  const results = new Map<string, FloorplanSnapshot>()

  // 1. Zkusíme načíst z Firestore (pudorys_project/projects/items)
  try {
    const user = await ensureAuthSession()
    if (user) {
      const qNew = query(
        collection(db, PUDORYS_ROOT_COLLECTION, 'projects', 'items'),
        where('authorId', '==', user.uid),
        limit(30)
      )
      const snap = await getDocs(qNew)
      snap.docs.forEach((d) => {
        const snapItem = buildFloorplanSnapshot(d.data(), d.id)
        results.set(snapItem.projectId, snapItem)
      })
    }
  } catch {
    // Ignorovat chybu při čtení
  }

  // 2. Zkusíme načíst i případné veřejně sdílené projekty v pudorys_project/projects/items
  try {
    const qShared = query(
      collection(db, PUDORYS_ROOT_COLLECTION, 'projects', 'items'),
      where('isShared', '==', true),
      limit(15)
    )
    const snapShared = await getDocs(qShared)
    snapShared.docs.forEach((d) => {
      if (!results.has(d.id)) {
        const snapItem = buildFloorplanSnapshot(d.data(), d.id)
        results.set(snapItem.projectId, snapItem)
      }
    })
  } catch {
    // Ignorovat
  }

  return Array.from(results.values())
}

/**
 * Načte konkrétní půdorys podle zadaného odkazu nebo kódu z Půdorys Online
 */
export async function fetchFloorplanByCodeOrUrl(rawInput: string): Promise<FloorplanSnapshot | null> {
  const projectId = parsePudorysIdFromInput(rawInput)
  if (!projectId) return null

  // Kontrola ukázkových šablon
  const demoMatch = DEMO_FLOORPLAN_TEMPLATES.find((t) => t.projectId === projectId)
  if (demoMatch) return demoMatch

  // 1. Hledání v nové kolekci pudorys_project/projects/items/{projectId}
  try {
    const refNew = doc(db, PUDORYS_ROOT_COLLECTION, 'projects', 'items', projectId)
    const snapNew = await getDoc(refNew)
    if (snapNew.exists()) {
      return buildFloorplanSnapshot(snapNew.data(), projectId)
    }
  } catch {
    // pokračujeme na starší kolekci
  }

  // 2. Hledání ve starší kolekci pudorys_projects/{projectId}
  try {
    const refOld = doc(db, PUDORYS_LEGACY_COLLECTION, projectId)
    const snapOld = await getDoc(refOld)
    if (snapOld.exists()) {
      return buildFloorplanSnapshot(snapOld.data(), projectId)
    }
  } catch {
    // ignorovat
  }

  return null
}

// ─── DATABÁZOVÁ VRSTVA PRO POPTÁVKY ONLINE (poptavky_project) ────────────────

function readLocalList<T>(key: string): T[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function writeLocalList<T>(key: string, items: T[]): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(key, JSON.stringify(items))
  } catch {
    // ignore quota error
  }
}

/**
 * Vyčistí stará falešná/ukázková demo data z LocalStorage,
 * aby systém zobrazoval výhradně skutečná data zadaná uživateli.
 */
export function cleanLegacyDemoData(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(LS_SEEDED_KEY)

    const companies = readLocalList<CompanyProfile>(LS_COMPANIES_KEY).filter(
      (c) =>
        !c.id.startsWith('comp_elektro_novak') &&
        !c.id.startsWith('comp_kuchyne_svoboda') &&
        !c.id.startsWith('comp_instal_kral') &&
        !c.ownerUid.startsWith('demo_')
    )
    writeLocalList(LS_COMPANIES_KEY, companies)

    const inquiries = readLocalList<Inquiry>(LS_INQUIRIES_KEY).filter(
      (i) => !i.id.startsWith('inq_demo_') && !i.clientUid.startsWith('demo_')
    )
    writeLocalList(LS_INQUIRIES_KEY, inquiries)

    const offers = readLocalList<InquiryOffer>(LS_OFFERS_KEY).filter(
      (o) => !o.id.startsWith('off_demo_')
    )
    writeLocalList(LS_OFFERS_KEY, offers)

    const transactions = readLocalList<CreditTransaction>(LS_TRANSACTIONS_KEY).filter(
      (t) => !t.id.startsWith('tx_demo_')
    )
    writeLocalList(LS_TRANSACTIONS_KEY, transactions)
  } catch {
    // Tichý fallback
  }
}

/** Zpětná kompatibilita – již nevytváří žádná falešná data */
export function ensureInitialDemoData(): void {
  cleanLegacyDemoData()
}

// ─── SYNCHRONIZAČNÍ FUNKCE (LocalStorage + Firestore poptavky_project) ────────

export function readCachedAllData(): {
  companies: CompanyProfile[]
  inquiries: Inquiry[]
  offers: InquiryOffer[]
  reviews: CompanyReview[]
  transactions: CreditTransaction[]
  users: AppUser[]
} {
  return {
    companies: readLocalList<CompanyProfile>(LS_COMPANIES_KEY),
    inquiries: readLocalList<Inquiry>(LS_INQUIRIES_KEY),
    offers: readLocalList<InquiryOffer>(LS_OFFERS_KEY),
    reviews: readLocalList<CompanyReview>(LS_REVIEWS_KEY),
    transactions: readLocalList<CreditTransaction>(LS_TRANSACTIONS_KEY),
    users: readLocalList<AppUser>(LS_USERS_KEY),
  }
}

export async function loadAllData(): Promise<{
  companies: CompanyProfile[]
  inquiries: Inquiry[]
  offers: InquiryOffer[]
  reviews: CompanyReview[]
  transactions: CreditTransaction[]
  users: AppUser[]
}> {
  cleanLegacyDemoData()

  const localCompanies = readLocalList<CompanyProfile>(LS_COMPANIES_KEY)
  const localInquiries = readLocalList<Inquiry>(LS_INQUIRIES_KEY)
  const localOffers = readLocalList<InquiryOffer>(LS_OFFERS_KEY)
  const localReviews = readLocalList<CompanyReview>(LS_REVIEWS_KEY)
  const localTransactions = readLocalList<CreditTransaction>(LS_TRANSACTIONS_KEY)
  const localUsers = auth.currentUser ? await loadAllAppUsers() : readLocalList<AppUser>(LS_USERS_KEY)

  // Pokus o rychlou neblokující synchronizaci z izolované kolekce `poptavky_project`
  try {
    const withTimeout = <T>(p: Promise<T>, ms = 3500): Promise<T | null> =>
      Promise.race([
        p,
        new Promise<null>((resolve) => setTimeout(() => resolve(null), ms)),
      ])

    const [compSnap, inqSnap, offSnap, revSnap, txSnap] = await Promise.all([
      withTimeout(getDocs(query(collection(db, ROOT_COLLECTION, 'companies', 'items'), limit(80)))),
      withTimeout(getDocs(query(collection(db, ROOT_COLLECTION, 'inquiries', 'items'), limit(80)))),
      withTimeout(getDocs(query(collection(db, ROOT_COLLECTION, 'offers', 'items'), limit(120)))),
      withTimeout(getDocs(query(collection(db, ROOT_COLLECTION, 'reviews', 'items'), limit(120)))),
      auth.currentUser
        ? withTimeout(getDocs(query(collection(db, ROOT_COLLECTION, 'transactions', 'items'), limit(120))))
        : Promise.resolve(null),
    ])

    const mergeById = <T extends { id: string; updatedAt?: number; createdAt?: number }>(
      local: T[],
      remote: T[]
    ): T[] => {
      const map = new Map<string, T>()
      local.forEach((item) => map.set(item.id, item))
      remote.forEach((item) => {
        const existing = map.get(item.id)
        if (!existing || (item.updatedAt || item.createdAt || 0) >= (existing.updatedAt || existing.createdAt || 0)) {
          map.set(item.id, item)
        }
      })
      return Array.from(map.values()).sort(
        (a, b) => (b.createdAt || 0) - (a.createdAt || 0)
      )
    }

    const remoteCompanies = compSnap ? compSnap.docs.map((d) => d.data() as CompanyProfile) : []
    const remoteInquiries = inqSnap ? inqSnap.docs.map((d) => d.data() as Inquiry) : []
    const remoteOffers = offSnap ? offSnap.docs.map((d) => d.data() as InquiryOffer) : []
    const remoteReviews = revSnap ? revSnap.docs.map((d) => d.data() as CompanyReview) : []
    const remoteTx = txSnap ? txSnap.docs.map((d) => d.data() as CreditTransaction) : []

    const companies = mergeById(localCompanies, remoteCompanies)
    const inquiries = mergeById(localInquiries, remoteInquiries)
    const offers = mergeById(localOffers, remoteOffers)
    const reviews = mergeById(localReviews, remoteReviews)
    const transactions = mergeById(localTransactions, remoteTx)

    writeLocalList(LS_COMPANIES_KEY, companies)
    writeLocalList(LS_INQUIRIES_KEY, inquiries)
    writeLocalList(LS_OFFERS_KEY, offers)
    writeLocalList(LS_REVIEWS_KEY, reviews)
    writeLocalList(LS_TRANSACTIONS_KEY, transactions)

    return { companies, inquiries, offers, reviews, transactions, users: localUsers }
  } catch {
    return {
      companies: localCompanies,
      inquiries: localInquiries,
      offers: localOffers,
      reviews: localReviews,
      transactions: localTransactions,
      users: localUsers,
    }
  }
}

export async function saveUserRecord(user: AppUser): Promise<void> {
  const list = readLocalList<AppUser>(LS_USERS_KEY)
  const idx = list.findIndex((u) => u.uid === user.uid)
  if (idx >= 0) list[idx] = user
  else list.unshift(user)
  writeLocalList(LS_USERS_KEY, list)

  try {
    ensurePoptavkyContainers().catch(() => {})
    await setDoc(doc(db, ROOT_COLLECTION, 'users', 'items', user.uid), user, {
      merge: true,
    })
  } catch {
    // Uloženo lokálně
  }
}

export async function saveCompanyRecord(company: CompanyProfile): Promise<void> {
  const list = readLocalList<CompanyProfile>(LS_COMPANIES_KEY)
  const idx = list.findIndex((c) => c.id === company.id)
  if (idx >= 0) list[idx] = company
  else list.unshift(company)
  writeLocalList(LS_COMPANIES_KEY, list)

  try {
    ensurePoptavkyContainers().catch(() => {})
    await setDoc(doc(db, ROOT_COLLECTION, 'companies', 'items', company.id), company, {
      merge: true,
    })
  } catch {
    // Uloženo lokálně
  }
}

export async function saveInquiryRecord(inquiry: Inquiry): Promise<void> {
  const list = readLocalList<Inquiry>(LS_INQUIRIES_KEY)
  const idx = list.findIndex((i) => i.id === inquiry.id)
  if (idx >= 0) list[idx] = inquiry
  else list.unshift(inquiry)
  writeLocalList(LS_INQUIRIES_KEY, list)

  try {
    ensurePoptavkyContainers().catch(() => {})
    await setDoc(doc(db, ROOT_COLLECTION, 'inquiries', 'items', inquiry.id), inquiry, {
      merge: true,
    })
  } catch {
    // Uloženo lokálně
  }
}

export async function saveOfferRecord(offer: InquiryOffer): Promise<void> {
  const list = readLocalList<InquiryOffer>(LS_OFFERS_KEY)
  const idx = list.findIndex((o) => o.id === offer.id)
  if (idx >= 0) list[idx] = offer
  else list.unshift(offer)
  writeLocalList(LS_OFFERS_KEY, list)

  try {
    ensurePoptavkyContainers().catch(() => {})
    await setDoc(doc(db, ROOT_COLLECTION, 'offers', 'items', offer.id), offer, {
      merge: true,
    })
  } catch {
    // Uloženo lokálně
  }
}

export async function saveReviewRecord(review: CompanyReview): Promise<void> {
  const list = readLocalList<CompanyReview>(LS_REVIEWS_KEY)
  const idx = list.findIndex((r) => r.id === review.id)
  if (idx >= 0) list[idx] = review
  else list.unshift(review)
  writeLocalList(LS_REVIEWS_KEY, list)

  try {
    ensurePoptavkyContainers().catch(() => {})
    await setDoc(doc(db, ROOT_COLLECTION, 'reviews', 'items', review.id), review, {
      merge: true,
    })
  } catch {
    // Uloženo lokálně
  }
}

export async function saveTransactionRecord(tx: CreditTransaction): Promise<void> {
  const list = readLocalList<CreditTransaction>(LS_TRANSACTIONS_KEY)
  const idx = list.findIndex((t) => t.id === tx.id)
  if (idx >= 0) list[idx] = tx
  else list.unshift(tx)
  writeLocalList(LS_TRANSACTIONS_KEY, list)

  try {
    ensurePoptavkyContainers().catch(() => {})
    await setDoc(doc(db, ROOT_COLLECTION, 'transactions', 'items', tx.id), tx, {
      merge: true,
    })
  } catch {
    // Uloženo lokálně
  }
}

export async function deleteInquiryRecord(inquiryId: string): Promise<void> {
  const list = readLocalList<Inquiry>(LS_INQUIRIES_KEY)
  const filtered = list.filter((i) => i.id !== inquiryId)
  writeLocalList(LS_INQUIRIES_KEY, filtered)

  try {
    await deleteDoc(doc(db, ROOT_COLLECTION, 'inquiries', 'items', inquiryId))
  } catch {
    // Smazáno lokálně
  }
}

const GENERATE_TEMPLATE_ENDPOINT =
  'https://europe-west1-hk-software-c5e52.cloudfunctions.net/generatePoptavkaTemplate'
const LS_AI_CACHE_KEY = 'poptavky_ai_template_cache_v1'

/**
 * Zavolá úspornou Cloud Function (gemini-3.8-flash) a vrátí předvyplněnou šablonu poptávky.
 * Používá lokální cache pro okamžitou odezvu a nulovou spotřebu při opakovaném dotazu.
 */
export async function generateAiInquiryTemplate(rawPrompt: string): Promise<AiInquiryDraft> {
  const cleanPrompt = rawPrompt.trim().replace(/\s+/g, ' ').slice(0, 350)
  if (!cleanPrompt) {
    throw new Error('Zadejte prosím, co potřebujete udělat nebo opravit.')
  }

  const cacheKey = cleanPrompt.toLowerCase()

  try {
    const rawCache = localStorage.getItem(LS_AI_CACHE_KEY)
    if (rawCache) {
      const cacheMap = JSON.parse(rawCache) as Record<string, AiInquiryDraft>
      if (cacheMap[cacheKey]) {
        return cacheMap[cacheKey]
      }
    }
  } catch {
    // Ignorovat chybu čtení cache
  }

  const response = await fetch(GENERATE_TEMPLATE_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ prompt: cleanPrompt }),
  })

  if (!response.ok) {
    throw new Error('Nepodařilo se automaticky připravit šablonu.')
  }

  const data = await response.json()
  if (!data?.ok || !data?.template) {
    throw new Error(data?.error || 'Nepodařilo se sestavit šablonu poptávky.')
  }

  const t = data.template
  const validCategoryIds = new Set(TRADE_CATEGORIES.map((c) => c.id))
  const parsedCategories: TradeCategory[] = Array.isArray(t.categories)
    ? t.categories.filter((c: string): c is TradeCategory =>
        validCategoryIds.has(c as TradeCategory)
      )
    : []

  const draft: AiInquiryDraft = {
    prompt: cleanPrompt,
    categories: parsedCategories.length > 0 ? parsedCategories : ['other'],
    title: String(t.title || cleanPrompt).trim(),
    description: String(t.description || cleanPrompt).trim(),
    subTasks: Array.isArray(t.subTasks)
      ? t.subTasks.map((s: unknown) => String(s).trim()).filter(Boolean)
      : [],
    scaleSummary: String(t.scaleSummary || 'Dle popisu').trim(),
    estimatedBudget: String(t.estimatedBudget || 'Dle nabídek').trim(),
    expectedDate: t.expectedDate ? String(t.expectedDate).trim() : undefined,
    city: t.city ? String(t.city).trim() : undefined,
  }

  try {
    const rawCache = localStorage.getItem(LS_AI_CACHE_KEY)
    const cacheMap: Record<string, AiInquiryDraft> = rawCache ? JSON.parse(rawCache) : {}
    cacheMap[cacheKey] = draft
    localStorage.setItem(LS_AI_CACHE_KEY, JSON.stringify(cacheMap))
  } catch {
    // Ignorovat chybu zápisu cache
  }

  return draft
}


